<template>
  <div>
    <!-- ── Main View Dialog ── -->
    <v-dialog v-model="dialog" eager persistent scrollable fullscreen>
      <v-form ref="InternRequestForm" @submit.prevent>
        <v-card class="fullscreen-card">
          <!-- Header -->
          <div class="dialog-header">
            <span class="text--white">HTE Intern Request</span>
            <v-spacer></v-spacer>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="closeD()"
              class="close-btn"
            />
          </div>

          <v-divider></v-divider>

          <v-card-text class="fullscreen-body">
            <div class="view-layout">
              <!-- Left: Request Details -->
              <div class="details-panel">
                <div class="panel-section">
                  <div class="panel-section-title">
                    <v-icon size="15" color="#16a34a"
                      >mdi-information-outline</v-icon
                    >
                    Request Details
                  </div>

                  <div class="detail-row">
                    <span class="detail-label">Number of Interns</span>
                    <span class="detail-value highlight">{{
                      num_interns
                    }}</span>
                  </div>

                  <div class="detail-row" v-if="programSelected.length > 0">
                    <span class="detail-label">Programs</span>
                    <div class="tags-wrap">
                      <span
                        class="prog-tag"
                        v-for="id in programSelected"
                        :key="id"
                      >
                        {{ getProgramAbbr(id) }}
                      </span>
                    </div>
                  </div>

                  <div class="detail-row">
                    <span class="detail-label">Student Batch</span>
                    <div class="tags-wrap">
                      <span class="prog-tag">
                        {{ getBatch }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Skills -->
                <div
                  class="panel-section"
                  v-if="skillRequired && skillsList.length > 0"
                >
                  <div class="panel-section-title">
                    <v-icon size="15" color="#2563eb"
                      >mdi-lightbulb-outline</v-icon
                    >
                    Required Skills
                  </div>
                  <div class="skills-wrap">
                    <span
                      class="skill-tag"
                      v-for="i in skillsList"
                      :key="i.id"
                      :class="{ active: selectedSkills.includes(i.title) }"
                    >
                      {{ i.title }}
                    </span>
                  </div>
                </div>

                <!-- Requested Students -->
                <div class="panel-section" v-if="selectedStudents.length > 0">
                  <div class="panel-section-title">
                    <v-icon size="15" color="#7c3aed"
                      >mdi-account-group-outline</v-icon
                    >
                    Requested Students
                    <span class="section-badge">{{ getStudents.length }}</span>
                  </div>
                  <div class="student-list">
                    <div
                      class="student-list-row"
                      v-for="(name, idx) in getStudents"
                      :key="idx"
                    >
                      <div class="student-list-avatar">
                        {{ getInitials(name) }}
                      </div>
                      <span class="student-list-name">{{ name }}</span>
                      <span class="student-num">#{{ idx + 1 }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <div class="dialog-footer" v-if="status == 1">
            <v-btn class="footer-cancel-btn" elevation="0" @click="closeD()">
              Close
            </v-btn>

            <!-- Request is only a number of interns: approve / deny -->
            <template v-if="isCountOnly">
              <v-btn
                class="footer-deny-btn"
                elevation="0"
                :disabled="btnLoading"
                @click="openDeny()"
              >
                <v-icon start size="16">mdi-close-circle</v-icon>
                Deny
              </v-btn>
              <v-btn
                class="footer-submit-btn"
                elevation="0"
                :disabled="btnLoading"
                @click="requestAction('grant')"
              >
                <v-icon start size="16">mdi-check-circle</v-icon>
                Approve
              </v-btn>
            </template>

            <!-- Request names specific students: review them -->
            <v-btn
              v-else
              class="footer-submit-btn"
              elevation="0"
              :disabled="btnLoading"
              :loading="btnLoading"
              @click="requestAction('grant')"
            >
              <v-icon start size="16">mdi-check-circle</v-icon>
              Review Request
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- ── Deny Dialog (number-only requests) ── -->
    <v-dialog v-model="denyDialog" eager persistent max-width="520">
      <v-form ref="DenyRequestForm" @submit.prevent>
        <v-card class="dialog-card">
          <div class="dialog-header">
            <span class="text-white">Deny Request</span>
            <v-spacer></v-spacer>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="closeDeny()"
              class="close-btn"
            />
          </div>

          <v-divider></v-divider>

          <v-card-text class="deny-body">
            <div class="deny-text">
              Please explain why this request is being denied.
            </div>
            <div class="section-label">
              <v-icon size="15" color="#374151"
                >mdi-comment-text-outline</v-icon
              >
              Remarks
              <span class="required-badge">Required</span>
            </div>
            <v-textarea
              v-model="denyRemarks"
              :rules="[formRules.required]"
              placeholder="Reason for denying this request..."
              :color="'#dc2626'"
              variant="outlined"
              density="compact"
              rows="4"
              hide-details="auto"
              class="remarks-field"
            ></v-textarea>
          </v-card-text>

          <v-divider></v-divider>

          <div class="dialog-footer">
            <v-btn
              class="footer-cancel-btn"
              elevation="0"
              :disabled="btnLoading"
              @click="closeDeny()"
            >
              Cancel
            </v-btn>
            <v-btn
              class="footer-danger-btn"
              elevation="0"
              :disabled="btnLoading"
              :loading="btnLoading"
              @click="confirmDeny()"
            >
              <v-icon start size="16">mdi-close-circle</v-icon>
              Deny Request
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- ── Remarks / Assignment Dialog ── -->
    <v-dialog
      v-model="remarksDialog"
      eager
      persistent
      scrollable
      :max-width="remarksDialogWidth"
    >
      <v-form ref="RemarksRequestForm" @submit.prevent>
        <v-card class="dialog-card">
          <!-- Header -->
          <div class="dialog-header">
            <span class="text-white">
              {{
                isReviewMode
                  ? "Review Requested Students"
                  : "Assign Recommended Students"
              }}
            </span>
            <v-spacer></v-spacer>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="closeRemarks()"
              class="close-btn"
            />
          </div>

          <v-divider></v-divider>

          <v-card-text class="remarks-body">
            <!-- Slots notice (requested vs. selected) -->
            <div v-if="slotNotice" class="slot-notice" :class="slotNotice.type">
              <v-icon size="15" :color="slotNotice.color">{{
                slotNotice.icon
              }}</v-icon>
              <span>{{ slotNotice.text }}</span>
            </div>

            <!-- Drag columns -->
            <div class="drag-columns" :class="{ three: showAvailable }">
              <!-- Left column: Requested / Recommended -->
              <div class="drag-col">
                <div class="drag-col-header green">
                  <v-icon size="16" color="#fff">mdi-account-check</v-icon>
                  <span>{{
                    isReviewMode ? "Requested Students" : "Recommended Students"
                  }}</span>

                  <!-- Spinner while recommendations are loading -->
                  <v-progress-circular
                    v-if="isRecommendationsLoading"
                    indeterminate
                    size="14"
                    width="2"
                    color="#fff"
                    class="ml-auto"
                  />
                  <span v-else class="col-count"
                    >{{
                      searchLeft
                        ? countMatches(leftList, searchLeft) + " / "
                        : ""
                    }}{{ leftList.length }}</span
                  >
                </div>

                <!-- Search (long recommended list only) -->
                <div
                  class="drag-search"
                  v-if="!isReviewMode && !isRecommendationsLoading"
                >
                  <v-text-field
                    v-model="searchLeft"
                    placeholder="Search student..."
                    prepend-inner-icon="mdi-magnify"
                    variant="outlined"
                    density="compact"
                    hide-details
                    clearable
                    class="search-field"
                  ></v-text-field>
                </div>

                <!-- Loading state -->
                <div v-if="isRecommendationsLoading" class="drag-loading">
                  <v-progress-circular
                    indeterminate
                    size="28"
                    width="3"
                    color="#16a34a"
                  />
                  <span>Finding recommended students...</span>
                </div>

                <draggable
                  v-else
                  class="drag-zone"
                  :list="leftList"
                  :group="{ name: 'my-group', put: true }"
                  drag-class="dragging"
                  chosen-class="chosen"
                  item-key="id"
                >
                  <template #item="{ element }">
                    <div
                      class="drag-item"
                      v-show="matchesSearch(element, searchLeft)"
                    >
                      <div class="drag-avatar green-av">
                        {{ element.fname[0] }}{{ element.lname[0] }}
                      </div>
                      <div class="drag-info">
                        <div class="drag-name">
                          {{ element.fname }} {{ element.lname }}
                        </div>
                        <div class="drag-status">
                          {{ getInternShipStatus(element.internship_status) }}
                        </div>
                      </div>
                      <v-icon size="14" color="#9ca3af" class="drag-handle"
                        >mdi-drag-vertical</v-icon
                      >
                    </div>
                  </template>
                  <template #footer>
                    <div class="drag-empty" v-if="leftList.length === 0">
                      <v-icon size="24" color="#d1d5db"
                        >mdi-account-off-outline</v-icon
                      >
                      <span>No students here</span>
                    </div>
                    <div
                      class="drag-empty"
                      v-else-if="
                        searchLeft && countMatches(leftList, searchLeft) === 0
                      "
                    >
                      <v-icon size="24" color="#d1d5db"
                        >mdi-magnify-close</v-icon
                      >
                      <span>No students match "{{ searchLeft }}"</span>
                    </div>
                  </template>
                </draggable>
              </div>

              <!-- Middle column: Available students (always shown in review
                   mode so a denied student can be replaced) -->
              <div class="drag-col" v-if="showAvailable">
                <div class="drag-col-header blue">
                  <v-icon size="16" color="#fff">mdi-account-search</v-icon>
                  <span>Available Students</span>
                  <v-progress-circular
                    v-if="isAvailableLoading"
                    indeterminate
                    size="14"
                    width="2"
                    color="#fff"
                    class="ml-auto"
                  />
                  <span v-else class="col-count"
                    >{{
                      searchAvailable
                        ? countMatches(recommended_students, searchAvailable) +
                          " / "
                        : ""
                    }}{{ recommended_students.length }}</span
                  >
                </div>

                <div class="drag-search" v-if="!isAvailableLoading">
                  <v-text-field
                    v-model="searchAvailable"
                    placeholder="Search student..."
                    prepend-inner-icon="mdi-magnify"
                    variant="outlined"
                    density="compact"
                    hide-details
                    clearable
                    class="search-field"
                  ></v-text-field>
                </div>

                <div v-if="isAvailableLoading" class="drag-loading">
                  <v-progress-circular
                    indeterminate
                    size="28"
                    width="3"
                    color="#2563eb"
                  />
                  <span>Finding available students...</span>
                </div>

                <draggable
                  v-else
                  class="drag-zone"
                  :list="recommended_students"
                  :group="{ name: 'my-group', put: true }"
                  drag-class="dragging"
                  chosen-class="chosen"
                  item-key="id"
                >
                  <template #item="{ element }">
                    <div
                      class="drag-item assign"
                      v-show="matchesSearch(element, searchAvailable)"
                    >
                      <div class="drag-avatar blue-av">
                        {{ element.fname[0] }}{{ element.lname[0] }}
                      </div>
                      <div class="drag-info">
                        <div class="drag-name">
                          {{ element.fname }} {{ element.lname }}
                        </div>
                        <div class="drag-status">
                          {{ getInternShipStatus(element.internship_status) }}
                          <span
                            v-if="element.match_score > 0"
                            :class="{ 'low-match': element.match_score < 50 }"
                          >
                            · {{ Math.round(element.match_score) }}% skill match
                          </span>
                        </div>
                      </div>
                      <v-icon size="14" color="#9ca3af" class="drag-handle"
                        >mdi-drag-vertical</v-icon
                      >
                    </div>
                  </template>
                  <template #footer>
                    <div
                      class="drag-empty"
                      v-if="recommended_students.length === 0"
                    >
                      <v-icon size="24" color="#d1d5db"
                        >mdi-account-off-outline</v-icon
                      >
                      <span>No available students</span>
                    </div>
                    <div
                      class="drag-empty"
                      v-else-if="
                        searchAvailable &&
                        countMatches(recommended_students, searchAvailable) ===
                          0
                      "
                    >
                      <v-icon size="24" color="#d1d5db"
                        >mdi-magnify-close</v-icon
                      >
                      <span>No students match "{{ searchAvailable }}"</span>
                    </div>
                  </template>
                </draggable>
              </div>

              <!-- Right column: Denied / To Assign -->
              <div class="drag-col">
                <div
                  class="drag-col-header"
                  :class="isReviewMode ? 'red' : 'blue'"
                >
                  <v-icon size="16" color="#fff">{{
                    isReviewMode
                      ? "mdi-account-remove"
                      : "mdi-account-arrow-right"
                  }}</v-icon>
                  <span>{{
                    isReviewMode ? "Denied Students" : "Students to Assign"
                  }}</span>
                  <span class="col-count">{{ deniedList.length }}</span>
                </div>
                <draggable
                  class="drag-zone"
                  :list="deniedList"
                  :group="'my-group'"
                  item-key="id"
                >
                  <template #item="{ element }">
                    <div
                      class="drag-item"
                      :class="isReviewMode ? 'denied' : 'assign'"
                    >
                      <div
                        class="drag-avatar"
                        :class="isReviewMode ? 'red-av' : 'blue-av'"
                      >
                        {{ element.fname[0] }}{{ element.lname[0] }}
                      </div>
                      <div class="drag-info">
                        <div class="drag-name">
                          {{ element.fname }} {{ element.lname }}
                        </div>
                        <div class="drag-status">
                          {{ getInternShipStatus(element.internship_status) }}
                        </div>
                      </div>
                      <v-icon size="14" color="#9ca3af" class="drag-handle"
                        >mdi-drag-vertical</v-icon
                      >
                    </div>
                  </template>
                  <template #footer>
                    <div class="drag-empty" v-if="deniedList.length === 0">
                      <v-icon size="24" color="#d1d5db"
                        >mdi-tray-arrow-down</v-icon
                      >
                      <span>Drag here</span>
                    </div>
                  </template>
                </draggable>
              </div>
            </div>

            <!-- Denied count notice (review mode only) -->
            <div class="denied-notice" v-if="remarksRequired">
              <v-icon size="15" color="#d97706">mdi-alert-outline</v-icon>
              <span
                >{{ deniedList.length }} student(s) marked as denied — remarks
                are required.</span
              >
            </div>

            <!-- Remarks -->
            <div class="remarks-section">
              <div class="section-label">
                <v-icon size="15" color="#374151"
                  >mdi-comment-text-outline</v-icon
                >
                Remarks
                <span class="required-badge" v-if="remarksRequired"
                  >Required</span
                >
                <span class="optional-badge" v-else>Optional</span>
              </div>
              <v-textarea
                v-model="remarks"
                :rules="remarksRequired ? [formRules.required] : []"
                placeholder="Add remarks about this request review..."
                :color="'#16a34a'"
                variant="outlined"
                density="compact"
                rows="3"
                hide-details="auto"
                class="remarks-field"
              ></v-textarea>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <div class="dialog-footer">
            <v-btn
              class="footer-cancel-btn"
              elevation="0"
              :disabled="btnLoading"
              @click="closeRemarks()"
            >
              Cancel
            </v-btn>
            <!-- Save only when the granted students match the number requested -->
            <v-btn
              class="footer-submit-btn"
              elevation="0"
              :disabled="
                btnLoading || isLoadingRecommendedStudents || !slotsSatisfied
              "
              :loading="btnLoading"
              @click="save()"
            >
              <v-icon start size="16">mdi-check-circle</v-icon>
              Save Review
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    ></fade-away-message-component>
  </div>
</template>

<script>
import eventBus from "@/eventBus";
import draggable from "vuedraggable";
export default {
  props: { data: Object, action: String },
  components: { draggable },
  data() {
    return {
      remarksDialog: false,
      denyDialog: false,
      denyRemarks: null,
      dialog: false,
      requestActionType: null,
      // 'review' = the request names specific students (review/deny them)
      // 'assign' = the request is only a number of interns (pick students)
      // Set once when the dialog opens so the labels don't flip while dragging.
      reviewMode: "review",
      // Show the "Available Students" column (always on in review mode so a
      // denied student can be replaced)
      showAvailable: false,
      // Search boxes for the long student lists
      searchLeft: "",
      searchAvailable: "",
      btnLoading: false,
      name: null,
      programsList: [],
      batch_selected: null,
      batchList: [],
      programSelected: [],
      studentList: [],
      num_interns: null,
      skillRequired: false,
      skillsList: [],
      selectedStudents: [],
      selectedSkills: [],
      allowedStudents: [],
      requested_studentsList: [],
      status: null,
      remarks: null,
      itemSelected: null,
      deniedList: [],
      recommended_students: [],
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "Successfully Added!",
        message: "",
        top: 10,
      },
      isLoadingRecommendedStudents: false,
    };
  },
  computed: {
    getStudents() {
      let req = this.data.st_data;
      let arr = [];
      for (let i = 0; i < req.length; i++) {
        let name = this.toTitleCase(
          req[i].fname +
            " " +
            (req[i].mname != "" && req[i].mname != null
              ? req[i].mname[0] + ". "
              : "") +
            req[i].lname,
        );
        arr.push(name);
      }
      return arr;
    },
    getBatch() {
      if (this.batch_selected) {
        const found = this.batchList.find(
          (item) => item.id == this.batch_selected,
        );
        return found ? found.name : "";
      }
      return "";
    },

    // The request is only a number of interns (no specific students named)
    isCountOnly() {
      return (
        !Array.isArray(this.selectedStudents) ||
        this.selectedStudents.length === 0
      );
    },
    isReviewMode() {
      return this.reviewMode === "review";
    },
    requiredCount() {
      return Number(this.num_interns) || 0;
    },
    // Left column list: requested students (review) or recommended (assign)
    leftList() {
      return this.isReviewMode
        ? this.requested_studentsList
        : this.recommended_students;
    },
    // Left column is waiting on recommendations (assign mode only)
    isRecommendationsLoading() {
      return !this.isReviewMode && this.isLoadingRecommendedStudents;
    },
    // Available column is waiting on the backend (review mode only)
    isAvailableLoading() {
      return (
        this.isReviewMode &&
        this.showAvailable &&
        this.isLoadingRecommendedStudents
      );
    },
    // Remarks are required only when students are denied while reviewing
    remarksRequired() {
      return this.isReviewMode && this.deniedList.length > 0;
    },
    remarksDialogWidth() {
      return this.showAvailable ? 1040 : 760;
    },

    // Students that will actually be granted if the admin saves now:
    //  - review mode: whoever is left in "Requested Students"
    //  - assign mode: whoever was dragged into "Students to Assign"
    grantedCount() {
      return this.isReviewMode
        ? this.requested_studentsList.length
        : this.deniedList.length;
    },
    // Save is only allowed when the granted students match the number requested
    slotsSatisfied() {
      return this.requiredCount > 0 && this.grantedCount === this.requiredCount;
    },

    // Tells the admin how many slots are filled compared to the request
    slotNotice() {
      const need = this.requiredCount;
      if (!need) return null;

      const have = this.grantedCount;

      if (have > need) {
        return {
          type: "warn",
          icon: "mdi-alert-outline",
          color: "#d97706",
          text: this.isReviewMode
            ? `${have} students are selected, but the request is for ${need}. Move ${
                have - need
              } to Denied Students.`
            : `${have} students assigned, but the request is for ${need}. Remove ${
                have - need
              }.`,
        };
      }

      if (have < need) {
        return {
          type: "info",
          icon: "mdi-information-outline",
          color: "#2563eb",
          text: this.isReviewMode
            ? `${have} of ${need} requested interns selected. Drag ${
                need - have
              } more from Available Students.`
            : `Assigned ${have} of ${need} requested interns. Drag ${
                need - have
              } more.`,
        };
      }

      return {
        type: "ok",
        icon: "mdi-check-circle-outline",
        color: "#16a34a",
        text: `All ${need} intern slots are filled.`,
      };
    },
  },
  watch: {
    data: {
      handler(data) {
        this.dialog = true;
        this.getProgsAndBatch();
        this.$refs.InternRequestForm.resetValidation();
        if (data.id) {
          this.selectedStudents = data.student_requested;
          this.selectedSkills = data.skills;
          this.programSelected = data.programs;
          this.num_interns = data.num_interns;
          this.status = data.status;
          this.allowedStudents = data.student_requested;
          this.batch_selected = data.student_batch_id;
          this.loadSkills();
        } else {
          this.$refs.InternRequestForm.reset();
        }
      },
      deep: true,
    },
  },
  methods: {
    // Case-insensitive name search. Every word typed must appear in the name,
    // so "abiera james" and "james, abiera" both find "James Carl Abiera".
    // Items are hidden with v-show (not filtered out of the array) so the
    // drag-and-drop indexes stay in sync with the underlying list.
    matchesSearch(student, query) {
      const q = (query || "").trim().toLowerCase();
      if (!q) return true;
      const name = `${student.fname || ""} ${student.mname || ""} ${
        student.lname || ""
      }`.toLowerCase();
      return q
        .replace(/,/g, " ")
        .split(/\s+/)
        .filter(Boolean)
        .every((term) => name.includes(term));
    },

    countMatches(list, query) {
      return list.filter((s) => this.matchesSearch(s, query)).length;
    },

    getInitials(fullName) {
      const parts = fullName.trim().split(" ");
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : fullName[0].toUpperCase();
    },

    getProgramAbbr(id) {
      if (id) {
        const found = this.programsList.find((item) => item.id == id);
        return found ? found.abbr : "";
      }
      return "";
    },

    getRequestedStudents() {
      let arr = [];
      for (let i = 0; i < this.studentList.length; i++) {
        for (let j = 0; j < this.selectedStudents.length; j++) {
          if (this.selectedStudents[j] == this.studentList[i].id) {
            arr.push(this.studentList[i]);
          }
        }
      }
      this.requested_studentsList = arr;
    },

    getIsChecked(skill) {
      return this.selectedSkills.includes(skill);
    },

    loadSkills() {
      let progs = JSON.stringify(this.programSelected);
      if (this.programSelected.length > 0) {
        this.axiosCall(
          "/skill-assessement/getSkillCategoryByProgram/" + progs,
          "GET",
        ).then((res) => {
          if (res.data) {
            this.skillsList = res.data.data;
            this.skillRequired = res.data.isRequired;
            let data = res.data.students;
            data.forEach((element, i) => {
              data[i].fname = this.toTitleCase(element.fname);
              data[i].lname = this.toTitleCase(element.lname);
            });
            this.studentList = data;
          }
        });
      }
    },

    // Opens the review / assignment dialog.
    //  - number-only request   -> assign mode, load recommended students
    //  - request with students -> review mode; the Available Students column
    //    is always shown so a denied student can be replaced and the granted
    //    count can still match the number of interns requested
    requestAction(type) {
      this.requestActionType = type;
      this.deniedList = [];
      this.remarks = null;
      this.recommended_students = [];
      this.showAvailable = false;
      this.searchLeft = "";
      this.searchAvailable = "";

      if (this.isCountOnly) {
        this.reviewMode = "assign";
        this.requested_studentsList = [];
        this.loadStudentBasedOnRequest();
      } else {
        this.reviewMode = "review";
        this.getRequestedStudents();
        this.showAvailable = true;
        this.loadStudentBasedOnRequest();
      }

      this.remarksDialog = true;
    },

    async loadStudentBasedOnRequest() {
      const requestId = this.data.id;
      this.isLoadingRecommendedStudents = true;
      this.recommended_students = []; // clear stale results from a previous request
      try {
        // Assign mode: only students who match the required skills (>= 50%).
        // Review mode (filling the remaining slots): every student who
        // completed the assessment, best skill match first, so the admin
        // can still pick someone below 50% if nobody reaches it.
        const res = await this.axiosCall(
          "/student/find-intern/byRequest/" +
            requestId +
            (this.isReviewMode ? "?min_match=0" : ""),
          "GET",
        );
        // ignore the response if the user has moved on to another request
        if (requestId === this.data.id && Array.isArray(res.data)) {
          // don't offer students that are already in the requested list
          const taken = new Set(this.requested_studentsList.map((s) => s.id));
          this.recommended_students = res.data
            .filter((s) => !taken.has(s.id))
            .map((s) => ({
              ...s,
              fname: this.toTitleCase(s.fname || ""),
              lname: this.toTitleCase(s.lname || ""),
            }));
        }
      } catch (error) {
        console.error(error);
        if (requestId === this.data.id) {
          this.showMessage("error", "Failed to load recommended students.");
        }
      } finally {
        if (requestId === this.data.id) {
          this.isLoadingRecommendedStudents = false;
        }
      }
    },

    async getProgsAndBatch() {
      try {
        const [progsRes, batchRes] = await Promise.all([
          this.axiosCall("/program", "GET"),
          this.axiosCall("/student/get-institute-batch-list", "GET"),
        ]);

        this.programsList = progsRes.data;
        this.batchList = batchRes.data;
      } catch (error) {
        console.error(error);
      }
    },

    closeRemarks() {
      this.requestActionType = null;
      this.searchLeft = "";
      this.searchAvailable = "";
      this.remarksDialog = false;
      this.isLoadingRecommendedStudents = false;
    },

    openDeny() {
      this.denyRemarks = null;
      this.denyDialog = true;
      this.$nextTick(() => this.$refs.DenyRequestForm?.resetValidation());
    },

    closeDeny() {
      this.denyDialog = false;
      this.denyRemarks = null;
    },

    getInternShipStatus(status) {
      const map = {
        null: "Available",
        1: "Requested",
        2: "Deployed",
        3: "Completed",
        4: "Terminated",
      };
      return map[status] ?? "Available";
    },

    showMessage(type, message) {
      this.fadeAwayMessage.show = true;
      this.fadeAwayMessage.type = type;
      this.fadeAwayMessage.header = "System Message";
      this.fadeAwayMessage.message = message;
    },

    // Shared by the review/assign save and the deny action
    async submitReview(data) {
      this.btnLoading = true;
      try {
        const res = await this.axiosCall(
          "/organization/grant-intern-request",
          "POST",
          data,
        );
        if (res.data.status == 200) {
          this.showMessage("success", res.data.msg);
          this.closeD();
        } else if (res.data.status == 400) {
          this.showMessage("error", res.data.msg);
        }
      } catch (error) {
        console.error(error);
        this.showMessage("error", "Something went wrong. Please try again.");
      } finally {
        this.btnLoading = false;
      }
    },

    async save() {
      // Hard guard in case the button state is bypassed
      if (!this.slotsSatisfied) {
        this.showMessage(
          "error",
          `Exactly ${this.requiredCount} intern(s) must be selected before saving. Currently ${this.grantedCount}.`,
        );
        return;
      }

      const validate = await this.$refs.RemarksRequestForm.validate();
      if (!validate.valid) return;

      // Review mode: left list = granted, right list = denied.
      // Assign mode: the students dragged into "Students to Assign" are the
      // ones being granted (the backend endorses everyone in student_granted).
      const granted = this.isReviewMode
        ? this.requested_studentsList
        : this.deniedList;
      const denied = this.isReviewMode ? this.deniedList : [];

      await this.submitReview({
        request_id: this.data.id,
        student_granted: JSON.stringify(granted),
        student_denied: JSON.stringify(denied),
        remarks: this.remarks,
      });
    },

    async confirmDeny() {
      const validate = await this.$refs.DenyRequestForm.validate();
      if (!validate.valid) return;

      await this.submitReview({
        request_id: this.data.id,
        student_granted: JSON.stringify([]),
        student_denied: JSON.stringify([]),
        remarks: this.denyRemarks,
      });
    },

    closeD() {
      eventBus.emit("closeCompanyInternRequestDialog", true);
      this.btnLoading = false;
      this.isLoadingRecommendedStudents = false;
      this.dialog = false;
      this.remarksDialog = false;
      this.denyDialog = false;
      this.denyRemarks = null;
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap");

/* ── Shared ── */
.dialog-card,
.fullscreen-card {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  border-radius: 16px !important;
  overflow: hidden;
}
.fullscreen-card {
  border-radius: 0 !important;
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: #fff;
}
.dialog-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}
.close-btn {
  color: #9ca3af !important;
}

/* ── Footer ── */
.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  background: #fff;
}
.footer-cancel-btn {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  background: #f3f4f6 !important;
  color: #374151 !important;
  border: 1px solid #e5e7eb !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 38px !important;
  padding: 0 18px !important;
}
.footer-submit-btn {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  border-radius: 8px !important;
  background: #16a34a !important;
  color: #fff !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 38px !important;
  padding: 0 20px !important;
}
.footer-deny-btn {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  border-radius: 8px !important;
  background: #fff !important;
  color: #dc2626 !important;
  border: 1px solid #fecaca !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 38px !important;
  padding: 0 20px !important;
}
.footer-deny-btn:hover {
  background: #fef2f2 !important;
}
.footer-danger-btn {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  border-radius: 8px !important;
  background: #dc2626 !important;
  color: #fff !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 38px !important;
  padding: 0 20px !important;
}

/* ── Main dialog body ── */
.fullscreen-body {
  background: #f4f6f8;
  padding: 28px !important;
  overflow-y: auto;
}
.view-layout {
  max-width: 640px;
  margin: 0 auto;
}

/* ── Details panel ── */
.details-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.panel-section {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.panel-section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.section-badge {
  margin-left: auto;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  padding: 1px 8px;
}
.detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f9fafb;
}
.detail-row:last-child {
  border-bottom: none;
}
.detail-label {
  font-size: 12px;
  color: #6b7280;
}
.detail-value {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.detail-value.highlight {
  background: #dcfce7;
  color: #15803d;
  padding: 2px 10px;
  border-radius: 6px;
  border: 1px solid #bbf7d0;
}

.tags-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.prog-tag {
  background: #dbeafe;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
}

.skills-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.skill-tag {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
}
.skill-tag.active {
  background: #dbeafe;
  color: #1d4ed8;
  border-color: #bfdbfe;
}

.student-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.student-list-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.student-list-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #ede9fe;
  color: #6d28d9;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.student-list-name {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.student-num {
  font-size: 11px;
  color: #9ca3af;
  background: #f3f4f6;
  border-radius: 4px;
  padding: 1px 6px;
}

/* ── Deny dialog ── */
.deny-body {
  padding: 20px !important;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f9fafb;
}
.deny-text {
  font-size: 13px;
  color: #4b5563;
}

/* ── Remarks dialog body ── */
.remarks-body {
  padding: 20px !important;
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-height: 560px;
  overflow-y: auto;
  background: #f9fafb;
}

/* ── Slots notice ── */
.slot-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid;
}
.slot-notice.info {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1e40af;
}
.slot-notice.ok {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}
.slot-notice.warn {
  background: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
}

/* ── Drag columns ── */
.drag-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.drag-columns.three {
  grid-template-columns: repeat(3, 1fr);
}
@media (max-width: 900px) {
  .drag-columns.three {
    grid-template-columns: 1fr;
  }
}
.drag-col {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}
.drag-col-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
}
.drag-col-header.green {
  background: #16a34a;
}
.drag-col-header.red {
  background: #dc2626;
}
.drag-col-header.blue {
  background: #2563eb;
}
.col-count {
  margin-left: auto;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 8px;
}

.drag-zone {
  min-height: 180px;
  /* long lists scroll inside the column so the search box stays in view */
  max-height: 340px;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* ── Search ── */
.drag-search {
  padding: 10px 10px 0;
}
.search-field :deep(.v-field) {
  border-radius: 8px !important;
  background: #fff !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 12px !important;
}
.search-field :deep(.v-field__outline__start),
.search-field :deep(.v-field__outline__end),
.search-field :deep(.v-field__outline__notch) {
  border-color: #d1d5db !important;
}
.search-field :deep(.v-field--focused .v-field__outline__start),
.search-field :deep(.v-field--focused .v-field__outline__end),
.search-field :deep(.v-field--focused .v-field__outline__notch) {
  border-color: #16a34a !important;
}
.search-field :deep(input) {
  font-size: 12px !important;
}

/* ── Loading state ── */
.drag-loading {
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

.drag-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: grab;
  transition: box-shadow 0.15s ease;
}
.drag-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.drag-item.denied {
  background: #fff5f5;
  border-color: #fecaca;
}
.drag-item.assign {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.drag-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.drag-avatar.green-av {
  background: #dcfce7;
  color: #15803d;
}
.drag-avatar.red-av {
  background: #fee2e2;
  color: #dc2626;
}
.drag-avatar.blue-av {
  background: #dbeafe;
  color: #1d4ed8;
}

.drag-info {
  flex: 1;
}
.drag-name {
  font-size: 12px;
  font-weight: 600;
  color: #111827;
}
.drag-status {
  font-size: 11px;
  color: #6b7280;
  margin-top: 1px;
}
.drag-handle {
  cursor: grab;
}
.low-match {
  color: #d97706;
  font-weight: 600;
}

.drag-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 24px 10px;
  font-size: 12px;
  color: #9ca3af;
}

/* drag states */
.dragging {
  opacity: 0.5;
}
.chosen {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12) !important;
  transform: scale(1.02);
}

/* ── Denied notice ── */
.denied-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 12px;
  color: #92400e;
  font-weight: 500;
}

/* ── Remarks section ── */
.remarks-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.required-badge {
  margin-left: 4px;
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 8px;
}
.optional-badge {
  margin-left: 4px;
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 8px;
}
.remarks-field :deep(.v-field) {
  border-radius: 8px !important;
  background: #fff !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
}
.remarks-field :deep(.v-field__outline__start),
.remarks-field :deep(.v-field__outline__end),
.remarks-field :deep(.v-field__outline__notch) {
  border-color: #d1d5db !important;
}
.remarks-field :deep(.v-field--focused .v-field__outline__start),
.remarks-field :deep(.v-field--focused .v-field__outline__end),
.remarks-field :deep(.v-field--focused .v-field__outline__notch) {
  border-color: #16a34a !important;
}
.remarks-field :deep(textarea) {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  color: #111827 !important;
}
</style>
